import { useState, useEffect, useContext } from 'react'
import DashboardLayout from 'components/layout/dashboard/DashboardLayout'
import { useTranslation } from 'react-i18next';
import './campaignList.css'
import '../../campaigns.css'
import AuthContext from 'contexte/AuthContext';
import { formatDate } from 'utils/date';
import Tableau from 'components/DataComponents/Tableau/Tableau';
import PageLoader from 'components/pageLoader/PageLoader';
import Notification from 'components/notifications/Notification';
import { launchSuccess, launchError } from 'components/notifications'
import { createCampaign, endCampaign, fetchCampaigns, updateCampaign } from 'services/campaignsServices';
import { formatNumberWithSpaces } from 'utils/numbers';
import ConfirmModal from 'components/ConfirmModal/ConfirmModal';
import CampaignFormModal from './CampaignFormModal';
import { CAMPAIGN_STATUS, CAMPAIGN_TYPES, CAMPAIGN_TYPES_SHORT, campaignStatus, campaignSummary } from '../../campaignTypes';

const LIMITS = [10, 25, 50]

const count = (value) => value === undefined || value === null ? '—' : formatNumberWithSpaces(value)
const euros = (value) => value === undefined || value === null ? '—' : `${formatNumberWithSpaces(value)} €`


const CampaignList = () => {

  const { t } = useTranslation()
  const { user } = useContext(AuthContext)

  const [page, setPage] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const [fetchLimit, setFetchLimit] = useState(10)
  const [campaignList, setCampaignList] = useState([])
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [toEdit, setToEdit] = useState(null) // campagne ouverte dans le formulaire, null = création
  const [toEnd, setToEnd] = useState(null)
  const [ending, setEnding] = useState(false)


  const fetchData = async () => {
    try {
      setError(false)
      const data = await fetchCampaigns(user, page, fetchLimit)
      setCampaignList(data)
    } catch (e) {
      console.error(e)
      setError(true)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [fetchLimit, page])

  // renvoie true si la campagne est créée, pour que le formulaire sache s'il peut se fermer
  const create = async (campaign) => {
    try {
      await createCampaign(user, campaign)
      launchSuccess(t('notifications.campaigns.create.success'))
      // la liste est triée par date de début côté API : on recharge la première page plutôt que d'insérer à la main
      if (page === 0) fetchData()
      else setPage(0)
      return true
    } catch (e) {
      launchError(t(`notifications.campaigns.create.${e?.response?.data?.error || 'error'}`, t('notifications.campaigns.create.error')))
      return false
    }
  }

  const update = async (campaign) => {
    try {
      await updateCampaign(user, toEdit._id, campaign)
      launchSuccess(t('notifications.campaigns.update.success'))
      // recharge la page courante : statistiques et nombre d'utilisateurs ciblés dépendent du segment
      fetchData()
      return true
    } catch (e) {
      launchError(t(`notifications.campaigns.update.${e?.response?.data?.error || 'error'}`, t('notifications.campaigns.update.error')))
      return false
    }
  }

  const openForm = (campaign = null) => {
    setToEdit(campaign)
    setIsModalOpen(true)
  }

  const confirmEnd = async () => {
    try {
      setEnding(true)
      const data = await endCampaign(user, toEnd._id)
      setCampaignList(prev => prev.map((campaign) => campaign._id === toEnd._id ? { ...campaign, end_date: data.end_date } : campaign))
      launchSuccess(t('notifications.campaigns.end.success'))
    } catch (e) {
      launchError(t(`notifications.campaigns.end.${e?.response?.data?.error || 'error'}`, t('notifications.campaigns.end.error')))
    } finally {
      setEnding(false)
      setToEnd(null)
    }
  }

  const tbody = (campaignList || []).map((item) => {
    const status = campaignStatus(item)
    const summary = campaignSummary(item)
    return {
      data: [
        <div className='campaigns_type_cell'>
          <span className='campaigns_list_name'>{item.name || 'Sans nom'}</span>
          {item.description && <span className='campaigns_list_description' title={item.description}>{item.description}</span>}
        </div>,
        <div className='campaigns_type_cell'>
          <span className='campaigns_chip'>{CAMPAIGN_TYPES_SHORT[item.type] || item.type}</span>
          {summary && <span className='campaigns_type_summary'>{summary}</span>}
        </div>,
        <span className={`campaigns_status campaigns_status_${status}`}>{CAMPAIGN_STATUS[status]}</span>,
        <span className='campaigns_num'>
          {item.end_date
            ? `${formatDate({ date: item.start_date })} → ${formatDate({ date: item.end_date })}`
            : `Dès le ${formatDate({ date: item.start_date })}`}
        </span>,
        <span className='campaigns_num'>{count(item.target_count)}</span>,
        <span className='campaigns_num'>{count(item.users_count)}</span>,
        <span className='campaigns_num'>{euros(item.costs)}</span>,
        <span className='campaigns_num'>{euros(item.volume)}</span>,
        <div className='campaigns_row_actions'>
          {/* les anciens types (hors CAMPAIGN_TYPES) ne sont plus gérés par le formulaire */}
          {status !== 'ended' && CAMPAIGN_TYPES[item.type] && (
            <button className='campaigns_row_button' aria-label={`Modifier la campagne « ${item.name || 'Sans nom'} »`}
              onClick={(e) => { e.stopPropagation(); openForm(item) }}>
              Modifier
            </button>
          )}
          {status !== 'ended' && (
            <button className='campaigns_row_button campaigns_row_button_danger' aria-label={`Terminer la campagne « ${item.name || 'Sans nom'} »`}
              onClick={(e) => { e.stopPropagation(); setToEnd(item) }}>
              Terminer
            </button>
          )}
        </div>
      ],
      utils: { id: item._id }
    }
  })


  return (
    <DashboardLayout page={t("pages_name.campaign_list")}>
      <div className='campaigns_page'>
        <Notification />
        <div className='campaigns_title_row'>
          <h1 className='campaigns_page_title'>Campagnes marketing</h1>
          <button className='campaigns_primary_button' onClick={() => openForm()}>Nouvelle campagne</button>
        </div>
        <p className='campaigns_hint'>Offres affichées dans l'app (cashback, frais réduits, gains levier) pour un segment d'utilisateurs. Une seule campagne par famille peut être active à la fois (levier, frais overnight, produit structuré), mais des familles différentes peuvent tourner en même temps.</p>

        {loading ? (
          <div className='pageLoaderBox'>
            <PageLoader />
          </div>
        ) : (
          <div className='campaigns_card'>
            {error ? (
              <div className='campaigns_empty campaigns_empty_error' role='alert'>
                <p className='campaigns_empty_title'>Erreur lors du chargement des campagnes</p>
                <button className='campaigns_secondary_button' onClick={fetchData}>Réessayer</button>
              </div>
            ) : tbody.length || page > 0 ? (
              <>
                <div className='campaigns_toolbar'>
                  <label htmlFor='campaigns-limit'>Lignes par page</label>
                  <select id='campaigns-limit' value={fetchLimit} onChange={(e) => { setFetchLimit(Number(e.target.value)); setPage(0) }}>
                    {LIMITS.map((value) => <option key={value} value={value}>{value}</option>)}
                  </select>
                </div>
                <div className='table-container'>
                  <div className='table-column'>
                    <Tableau
                      thead={['Nom', 'Type', 'Statut', 'Période', 'Utilisateurs ciblés', 'Utilisateurs', 'Coûts', 'Volume', 'Actions']}
                      tbody={tbody}
                    />
                  </div>
                  {(page > 0 || tbody.length >= fetchLimit) && (
                    <nav className='campaigns_pager' aria-label='Pagination'>
                      <button className='campaigns_row_button' disabled={page === 0} onClick={() => setPage(page - 1)}>Précédent</button>
                      <span className='campaigns_num' aria-live='polite'>Page {page + 1}</span>
                      <button className='campaigns_row_button' disabled={tbody.length < fetchLimit} onClick={() => setPage(page + 1)}>Suivant</button>
                    </nav>
                  )}
                </div>
              </>
            ) : (
              <div className='campaigns_empty'>
                <p className='campaigns_empty_title'>Aucune campagne</p>
                <p className='campaigns_empty_hint'>Créez une campagne avec le bouton « Nouvelle campagne ».</p>
              </div>
            )}
          </div>
        )}

        <CampaignFormModal
          isModalOpen={isModalOpen}
          campaign={toEdit}
          onClose={() => { setIsModalOpen(false); setToEdit(null) }}
          onSubmit={toEdit ? update : create}
        />

        <ConfirmModal
          isOpen={!!toEnd}
          loading={ending}
          title={toEnd && campaignStatus(toEnd) === 'upcoming' ? 'Annuler la campagne ?' : 'Terminer la campagne ?'}
          message={toEnd ? (campaignStatus(toEnd) === 'upcoming'
            ? `La campagne « ${toEnd.name || 'Sans nom'} » n'a pas encore commencé : elle ne démarrera pas. Cette action est définitive.`
            : `La campagne « ${toEnd.name || 'Sans nom'} » s'arrête immédiatement pour tous les utilisateurs.${toEnd.type === 'bonus' ? " Ce qui reste sur les bonus qu'elle a donnés est annulé ; la part déjà utilisée dans des positions ouvertes est gardée." : ''} Cette action est définitive.`) : ''}
          confirmLabel='Terminer'
          cancelLabel='Retour'
          onConfirm={confirmEnd}
          onClose={() => setToEnd(null)}
        />
      </div>
    </DashboardLayout>
  )
}


export default CampaignList
